/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Transition } from '@uirouter/core';
import { StateService } from '@uirouter/angular';
import { HallOfFameService, HallOfFamePeriod, HallOfFameCategory } from '../services/hall-of-fame.service';

interface NavigationParams {
  year: number;
  period: string;
}

@Component({
  selector: 'ut-hall-of-fame',
  templateUrl: './hall-of-fame.component.html',
  styleUrls: ['./hall-of-fame.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HallOfFameComponent implements OnInit {
  search = {
    get year(): number {
      return this._searchYear;
    },
    set year(value: any) {
      this._searchYear = Number(value) || 0;
    },
    period: '',
  };

  searchOptions = {
    years: [] as number[],
    periods: [] as string[],
  };

  data: HallOfFameCategory[] = [];
  isLoading = true;
  next?: NavigationParams;
  previous?: NavigationParams;

  private periods: HallOfFamePeriod[] = [];

  constructor(
    private transition: Transition,
    private stateService: StateService,
    private hallOfFameService: HallOfFameService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Get periods from route resolve
    this.periods = this.transition.injector().get('periods');

    // Get route params
    const params = this.transition.params();
    this.search.year = Number(params.year) || 0;
    this.search.period = params.period;

    // Build search options
    this.buildSearchOptions();

    // Set navigation (next/previous)
    this.setNavigation();

    // Load winners data
    this.loadWinners();
  }

  private buildSearchOptions(): void {
    // Get unique years
    const yearsSet = new Set<number>();
    this.periods.forEach((p) => yearsSet.add(p.year));
    this.searchOptions.years = Array.from(yearsSet).sort((a, b) => b - a);

    // Get periods for the selected year
    const selectedYearData = this.periods.find((p) => p.year === this.search.year);
    if (selectedYearData) {
      // Get all periods for this year
      const periodsForYear = this.periods
        .filter((p) => p.year === this.search.year)
        .map((p) => p.period);
      this.searchOptions.periods = Array.from(new Set(periodsForYear));
    }
  }

  private setNavigation(): void {
    const currentIndex = this.searchOptions.periods.indexOf(this.search.period);
    const nextIndex = currentIndex + 1;
    const prevIndex = currentIndex - 1;

    if (nextIndex < this.searchOptions.periods.length) {
      this.next = {
        year: this.search.year,
        period: this.searchOptions.periods[nextIndex],
      };
    } else {
      this.next = undefined;
    }

    if (prevIndex >= 0) {
      this.previous = {
        year: this.search.year,
        period: this.searchOptions.periods[prevIndex],
      };
    } else {
      this.previous = undefined;
    }
  }

  private loadWinners(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.hallOfFameService.getWinners(this.search.year, this.search.period).subscribe({
      next: (categories: HallOfFameCategory[]) => {
        // Backend already returns data in the correct format
        this.data = categories;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (_error) => {
        this.data = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onYearChange(): void {
    // Get the first period for the selected year
    const selectedYearData = this.periods.find((p) => p.year === this.search.year);

    if (selectedYearData) {
      const periodsForYear = this.periods
        .filter((p) => p.year === this.search.year)
        .map((p) => p.period);
      const firstPeriod = Array.from(new Set(periodsForYear))[0];

      this.stateService.go('halloffamedate', {
        year: this.search.year,
        period: firstPeriod,
      });
    }
  }

  onPeriodChange(): void {
    this.stateService.go('halloffamedate', {
      year: this.search.year,
      period: this.search.period,
    });
  }

  trackByUserId(index: number, user: any): number {
    return user.id || index;
  }
}
